import { MAIN_SITE_URL } from "@/lib/constants";
import { AnimatePresence, motion } from "framer-motion";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import useScroll from "@/lib/hooks/use-scroll";
import { useOrganization } from "@/lib/hooks/use-organization";
import Meta from "./meta";
import { useSignInModal } from "./sign-in-modal";
import UserDropdown from "./user-dropdown";
import classNames from "classnames";
import styles from "./index.module.css";
import { useSelector } from "react-redux";
import { RootState } from "@/stores/root-reducer";
import { selectAllTransactions } from "@/stores/transactions-store/transactions-selectors";
import { selectCurrentOrganization } from "@/stores/organizations-store/organizations-selectors";
import { selectAllMaintenanceRequests } from "@/stores/maintenance-store/maintenance-selectors";
import { TransactionFlyout } from "../shared/transaction-flyout";
import MaintenanceFlyout from "../maintenance/maintenance-flyout";
import Sidebar from "./sidebar";
import { FADE_IN_ANIMATION_SETTINGS } from "@/lib/constants";
import { useFlyoutContext } from "@/components/shared/atoms/toaster";
import { setFlyoutContext } from "@/components/shared/atoms/toaster";

export default function Layout({
  meta,
  children,
  hideLogin,
}: {
  meta?: {
    title?: string;
    description?: string;
    image?: string;
  };
  children: (props: {
    isToastVisible: boolean;
    openFlyout: ({
      transactionId,
    }: {
      transactionId: string | undefined;
    }) => void;
    openMaintenanceFlyout: ({
      maintenanceRequestId,
    }: {
      maintenanceRequestId: string | undefined;
    }) => void;
    toastMessage: string;
  }) => React.ReactNode;
  hideLogin?: boolean;
}) {
  const { data: session, status } = useSession();
  const { isLoading: isLoadingOrg } = useOrganization();
  const organization = useSelector(selectCurrentOrganization);
  const { setShowSignInModal, SignInModal } = useSignInModal();
  const scrolled = useScroll(50);
  const flyoutContext = useFlyoutContext();
  const { setIsFlyoutOpen, setIsFlyoutOpening } = flyoutContext;

  // Set the flyout context for the toaster
  React.useEffect(() => {
    setFlyoutContext(flyoutContext);
  }, [flyoutContext]);

  // flyout state variables
  const [isFlyoutOpen, setIsFlyoutOpenLocal] = useState<boolean>(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | undefined
  >();

  // maintenance flyout state variables
  const [isMaintenanceFlyoutOpen, setIsMaintenanceFlyoutOpen] =
    useState<boolean>(false);
  const [selectedMaintenanceRequestId, setSelectedMaintenanceRequestId] =
    useState<string | undefined>();

  const openFlyout = ({
    transactionId,
  }: {
    transactionId: string | undefined;
  }) => {
    setIsFlyoutOpening(true);
    setIsFlyoutOpenLocal(true);
    setIsFlyoutOpen(true);
    setSelectedTransactionId(transactionId);

    setTimeout(() => {
      setIsFlyoutOpening(false);
    }, 500);
  };

  const openMaintenanceFlyout = ({
    maintenanceRequestId,
  }: {
    maintenanceRequestId: string | undefined;
  }) => {
    setIsMaintenanceFlyoutOpen(true);
    setSelectedMaintenanceRequestId(maintenanceRequestId);
  };

  const closeFlyout = () => {
    setIsFlyoutOpenLocal(false);
    setIsFlyoutOpen(false);
  };

  const closeMaintenanceFlyout = () => {
    setIsMaintenanceFlyoutOpen(false);
    setSelectedMaintenanceRequestId(undefined);
  };

  // transactions selector
  const transactions = useSelector((state: RootState) =>
    selectAllTransactions(state),
  );

  // maintenance requests selector
  const maintenanceRequests = useSelector((state: RootState) =>
    selectAllMaintenanceRequests(state),
  );

  return (
    <>
      <Meta {...meta} />
      {!hideLogin && <SignInModal />}
      <div
        className={classNames(styles.headerContainer, {
          [styles.scrolled]: scrolled,
        })}
      >
        <div className={styles.headerContent}>
          {hideLogin ? (
            <div className={styles.logoContainer}>
              <Image
                src='/logo.png'
                alt='Rent.Today logo'
                width={30}
                height={30}
                className={styles.logoImage}
                priority
              />
              <p className={styles.logoText}>Rent.today</p>
            </div>
          ) : (
            <div className={styles.navContainer}>
              <Link href='/' className={styles.navLink}>
                <Image
                  src='/logo.png'
                  alt='Rent.Today logo'
                  width={30}
                  height={30}
                  className={styles.logoImage}
                  priority
                />
                <p className={styles.logoText}>Rent.today</p>
              </Link>
              {session && !isLoadingOrg && organization && (
                <div className={styles.orgInfo}>
                  <div className={styles.orgDivider} />
                  <p className={styles.orgName}>{organization.name}</p>
                </div>
              )}
            </div>
          )}
          {!hideLogin && (
            <div className={styles.user}>
              <AnimatePresence>
                {!session && status !== "loading" ? (
                  <motion.button
                    className={styles.loginButton}
                    onClick={() => setShowSignInModal(true)}
                    {...FADE_IN_ANIMATION_SETTINGS}
                  >
                    Log In
                  </motion.button>
                ) : (
                  <UserDropdown />
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
      <div className={styles.mainContent}>
        <div className={styles.mainWrapper}>
          {!hideLogin && <Sidebar />}
          <main className={styles.mainArea}>
            {typeof children === "function"
              ? children({
                  openFlyout,
                  openMaintenanceFlyout,
                  isToastVisible: false,
                  toastMessage: "",
                })
              : children}
          </main>
        </div>
        <div className={styles.footer}>
          Simplify Your Lease, Amplify Your Peace with
          <p className={styles.footerText}>
            <a
              className={styles.footerLink}
              href={MAIN_SITE_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              Rent.Today
            </a>
          </p>
        </div>
      </div>
      {/* Flyout here */}
      <div className={styles.flyout}>
        <TransactionFlyout
          key={selectedTransactionId || "new"}
          isOpen={isFlyoutOpen}
          setIsOpen={setIsFlyoutOpen}
          transactionId={selectedTransactionId}
          transactions={transactions}
          closeFlyout={closeFlyout}
        />
      </div>
      {/* Maintenance Flyout here */}
      <div className={styles.flyout}>
        <MaintenanceFlyout
          isOpen={isMaintenanceFlyoutOpen}
          setIsOpen={setIsMaintenanceFlyoutOpen}
          maintenanceRequestId={selectedMaintenanceRequestId}
          maintenanceRequests={maintenanceRequests}
          setSelectedMaintenanceRequestId={setSelectedMaintenanceRequestId}
          closeFlyout={closeMaintenanceFlyout}
        />
      </div>
    </>
  );
}
